import { useEffect, useState, useMemo } from 'react'
import { X, Loader2, TrendingDown, TrendingUp, MapPin } from 'lucide-react'
import type { Station } from '@fuelfinder/shared'
import { fetchAllStations } from '../lib/api'

interface UkStatsModalProps {
  open: boolean
  onClose: () => void
  fuelType: 'E10' | 'B7'
  onNavigateToStation: (lat: number, lng: number) => void
}

interface BrandStat {
  brand: string
  avgPrice: number
  count: number
}

interface ExtremeStation {
  station: Station
  price: number
}

/**
 * Normalise brand names so "JET"/"Jet", "ESSO"/"Esso", "SHELL"/"Shell" etc.
 * are merged into a single canonical name for aggregation.
 */
function normalizeBrand(raw: string): string {
  const upper = raw.trim().toUpperCase()

  const canonicalMap: Record<string, string> = {
    'ASDA': 'Asda',
    'ASDA EXPRESS': 'Asda',
    'BP': 'BP',
    'ESSO': 'Esso',
    'ESSO EXPRESS': 'Esso',
    'JET': 'Jet',
    'MORRISONS': 'Morrisons',
    'SAINSBURY\'S': "Sainsbury's",
    'SAINSBURYS': "Sainsbury's",
    'SHELL': 'Shell',
    'TEXACO': 'Texaco',
    'TESCO': 'Tesco',
    'VALERO': 'Valero',
  }

  if (canonicalMap[upper]) return canonicalMap[upper]

  // Fallback: title-case the raw name so at least casing is consistent
  return raw.trim().replace(/\w\S*/g, (w) =>
    w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  )
}

function computeStats(stations: Station[], fuelType: 'E10' | 'B7') {
  let cheapest: ExtremeStation | null = null
  let mostExpensive: ExtremeStation | null = null
  const brandTotals = new Map<string, { total: number; count: number }>()

  for (const station of stations) {
    const price = station.prices[fuelType]
    if (price === null) continue

    if (!cheapest || price < cheapest.price) {
      cheapest = { station, price }
    }
    if (!mostExpensive || price > mostExpensive.price) {
      mostExpensive = { station, price }
    }

    const brand = normalizeBrand(station.brand)
    const existing = brandTotals.get(brand)
    if (existing) {
      existing.total += price
      existing.count += 1
    } else {
      brandTotals.set(brand, { total: price, count: 1 })
    }
  }

  const brandStats: BrandStat[] = Array.from(brandTotals.entries())
    .filter(([, v]) => v.count >= 3)
    .map(([brand, v]) => ({
      brand,
      avgPrice: v.total / v.count,
      count: v.count,
    }))
    .sort((a, b) => a.avgPrice - b.avgPrice)

  return { cheapest, mostExpensive, brandStats }
}

function formatPrice(pricePence: number): string {
  return `${pricePence.toFixed(1)}p`
}

/**
 * Interpolate from green (#22c55e) through amber (#f59e0b) to red (#ef4444)
 * based on a 0..1 ratio.
 */
function priceColor(ratio: number): string {
  if (ratio <= 0.5) {
    const t = ratio * 2
    const r = Math.round(0x22 + (0xf5 - 0x22) * t)
    const g = Math.round(0xc5 + (0x9e - 0xc5) * t)
    const b = Math.round(0x5e + (0x0b - 0x5e) * t)
    return `rgb(${r},${g},${b})`
  }
  const t = (ratio - 0.5) * 2
  const r = Math.round(0xf5 + (0xef - 0xf5) * t)
  const g = Math.round(0x9e + (0x44 - 0x9e) * t)
  const b = Math.round(0x0b + (0x44 - 0x0b) * t)
  return `rgb(${r},${g},${b})`
}

export function UkStatsModal({ open, onClose, fuelType, onNavigateToStation }: UkStatsModalProps) {
  const [stations, setStations] = useState<Station[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedFuel, setSelectedFuel] = useState<'E10' | 'B7'>(fuelType)

  // Sync with the app-level fuel type when the modal opens
  useEffect(() => {
    if (open) setSelectedFuel(fuelType)
  }, [open, fuelType])

  useEffect(() => {
    if (!open) return
    let cancelled = false

    setLoading(true)
    setError(null)
    fetchAllStations()
      .then((data) => {
        if (!cancelled) setStations(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [open])

  const stats = useMemo(() => {
    if (!stations) return null
    return computeStats(stations, selectedFuel)
  }, [stations, selectedFuel])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              UK Stats
            </h2>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 min-h-[44px] rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close stats"
            >
              <X size={20} />
            </button>
          </div>

          {/* Fuel type toggle */}
          <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-1">
            <button
              onClick={() => setSelectedFuel('E10')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                selectedFuel === 'E10'
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
              aria-pressed={selectedFuel === 'E10'}
            >
              Petrol
            </button>
            <button
              onClick={() => setSelectedFuel('B7')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                selectedFuel === 'B7'
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
              aria-pressed={selectedFuel === 'B7'}
            >
              Diesel
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 size={28} className="text-green-600 animate-spin" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading UK station data...
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {stats && (
            <>
              {/* Cheapest station */}
              {stats.cheapest && (
                <button
                  onClick={() => {
                    onNavigateToStation(stats.cheapest!.station.lat, stats.cheapest!.station.lng)
                    onClose()
                  }}
                  className="w-full text-left rounded-lg border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown size={16} className="text-green-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
                      Cheapest in UK
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {stats.cheapest.station.brand}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                        <MapPin size={11} />
                        {stats.cheapest.station.address}
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-green-600">
                      {formatPrice(stats.cheapest.price)}
                    </span>
                  </div>
                </button>
              )}

              {/* Most expensive station */}
              {stats.mostExpensive && (
                <button
                  onClick={() => {
                    onNavigateToStation(stats.mostExpensive!.station.lat, stats.mostExpensive!.station.lng)
                    onClose()
                  }}
                  className="w-full text-left rounded-lg border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp size={16} className="text-red-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
                      Most Expensive in UK
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {stats.mostExpensive.station.brand}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                        <MapPin size={11} />
                        {stats.mostExpensive.station.address}
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-red-600">
                      {formatPrice(stats.mostExpensive.price)}
                    </span>
                  </div>
                </button>
              )}

              {/* Brand rankings */}
              {stats.brandStats.length > 0 && (
                <section>
                  <h3 className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-3">
                    Average Price by Brand
                  </h3>
                  <div className="space-y-1.5">
                    {stats.brandStats.map((brand, i) => {
                      const minPrice = stats.brandStats[0].avgPrice
                      const maxPrice = stats.brandStats[stats.brandStats.length - 1].avgPrice
                      const range = maxPrice - minPrice
                      const ratio = range > 0 ? (brand.avgPrice - minPrice) / range : 0
                      const barWidth = range > 0
                        ? 30 + ((brand.avgPrice - minPrice) / range) * 70
                        : 50

                      return (
                        <div key={brand.brand} className="flex items-center gap-2">
                          <span className="text-xs text-slate-700 dark:text-slate-300 w-28 truncate shrink-0 text-right" title={brand.brand}>
                            {brand.brand}
                          </span>
                          <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                            <div
                              className="h-full rounded transition-all duration-500"
                              style={{
                                width: `${barWidth}%`,
                                backgroundColor: priceColor(ratio),
                              }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 w-14 text-right tabular-nums">
                            {formatPrice(brand.avgPrice)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                    Brands with 3+ stations. {stations?.length.toLocaleString()} stations total.
                  </p>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
